import type { ScheduleDto } from '@/api/control/missionImpactAPI.schemas';

/**
 * Converts a TimeSpan string (HH:mm:ss) to minutes since midnight
 */
function timeSpanToMinutes(timeSpan: string): number {
  const [hours, minutes] = timeSpan.split(':').map(Number);
  return hours * 60 + minutes;
}

/**
 * Checks if a given UTC time falls within a quiet time window
 */
function isInQuietTime(
  dateTime: Date,
  startTime: string,
  endTime: string,
  daysFilter?: number[]
): boolean {
  const dayOfWeek = dateTime.getUTCDay();

  // For weekly schedules, check if current day is in the allowed days
  if (daysFilter !== undefined && !daysFilter.includes(dayOfWeek)) {
    return false;
  }

  const currentMinutes = dateTime.getUTCHours() * 60 + dateTime.getUTCMinutes();
  const startMinutes = timeSpanToMinutes(startTime);
  const endMinutes = timeSpanToMinutes(endTime);

  // Handle quiet time that spans midnight
  if (endMinutes < startMinutes) {
    // Quiet time wraps around midnight (e.g., 22:00 to 06:00)
    return currentMinutes >= startMinutes || currentMinutes < endMinutes;
  } else {
    // Normal quiet time within same day
    return currentMinutes >= startMinutes && currentMinutes < endMinutes;
  }
}

/**
 * Calculates the end of the quiet time period for a given date
 * Returns the UTC date/time when quiet time ends
 */
function getQuietTimeEnd(currentDate: Date, endTime: string, startTime: string): Date {
  const endMinutes = timeSpanToMinutes(endTime);
  const startMinutes = timeSpanToMinutes(startTime);
  const currentMinutes = currentDate.getUTCHours() * 60 + currentDate.getUTCMinutes();

  const result = new Date(currentDate);

  // If end time is less than start time, quiet time wraps around midnight
  if (endMinutes < startMinutes) {
    // If we're currently before the end time, we're still in today's quiet time
    if (currentMinutes < endMinutes) {
      result.setUTCHours(Math.floor(endMinutes / 60), endMinutes % 60, 0, 0);
    } else {
      // Otherwise, quiet time ends tomorrow
      result.setUTCDate(result.getUTCDate() + 1);
      result.setUTCHours(Math.floor(endMinutes / 60), endMinutes % 60, 0, 0);
    }
  } else {
    // Normal case: quiet time ends today
    result.setUTCHours(Math.floor(endMinutes / 60), endMinutes % 60, 0, 0);
  }

  return result;
}

/**
 * The accelerated burst interval, in minutes. Kept in sync with the ETL worker's
 * OrchestrationOptions.BurstIntervalMinutes (issue #443).
 */
const BURST_INTERVAL_MINUTES = 5;

/**
 * Calculates the next sync time given schedules with quiet time configuration.
 *
 * @param schedules - Array of schedule configurations (daily or weekly quiet times)
 * @param now - Current time (defaults to now)
 * @param burstExpiresUtc - The profile's burst expiry (ISO-8601 UTC), or null when no burst is active
 * @returns The next sync time as a Date object
 *
 * Logic:
 * - Normally syncs occur every hour on the hour.
 * - While a burst is active (burstExpiresUtc in the future), syncs occur every
 *   BURST_INTERVAL_MINUTES from now (unaligned), matching the trigger's burst cadence; once the
 *   burst has expired the schedule reverts to hourly.
 * - If the candidate tick falls within a quiet time window, skip past it (to the end of quiet time
 *   for a burst, or to the next available hour otherwise) — quiet time suppresses ticks either way.
 * - For daily quiet time: applies every day. For weekly quiet time: applies only on specified days.
 */
export function calculateNextSyncTime(
  schedules: ScheduleDto[],
  now: Date = new Date(),
  burstExpiresUtc: string | null = null
): Date {
  const burstEnd = burstExpiresUtc ? new Date(burstExpiresUtc) : null;
  const burstActive =
    burstEnd !== null && !Number.isNaN(burstEnd.getTime()) && burstEnd.getTime() > now.getTime();

  // Base candidate: during a burst the next tick is `now + interval` (unaligned); otherwise the
  // next top of the hour.
  let candidateTime: Date;
  if (burstActive) {
    candidateTime = new Date(now.getTime() + BURST_INTERVAL_MINUTES * 60 * 1000);
  } else {
    candidateTime = new Date(now);
    candidateTime.setUTCMinutes(0, 0, 0);
    candidateTime.setUTCHours(candidateTime.getUTCHours() + 1);
  }

  const maxIterations = 48; // Prevent infinite loops (check up to ~48 steps ahead)
  let iterations = 0;

  while (iterations < maxIterations) {
    let inQuietTime = false;
    let latestQuietEnd: Date | null = null;

    for (const schedule of schedules) {
      if (schedule.$type === 'QuietTimeDaily') {
        const dailySchedule = schedule;
        if (isInQuietTime(candidateTime, dailySchedule.startTime, dailySchedule.endTime)) {
          inQuietTime = true;
          const quietEnd = getQuietTimeEnd(candidateTime, dailySchedule.endTime, dailySchedule.startTime);
          if (!latestQuietEnd || quietEnd > latestQuietEnd) {
            latestQuietEnd = quietEnd;
          }
        }
      } else {
        const weeklySchedule = schedule;
        if (
          isInQuietTime(
            candidateTime,
            weeklySchedule.startTime,
            weeklySchedule.endTime,
            weeklySchedule.days
          )
        ) {
          inQuietTime = true;
          const quietEnd = getQuietTimeEnd(candidateTime, weeklySchedule.endTime, weeklySchedule.startTime);
          if (!latestQuietEnd || quietEnd > latestQuietEnd) {
            latestQuietEnd = quietEnd;
          }
        }
      }
    }

    if (!inQuietTime) {
      // If bursting but this tick lands after the burst has expired, the burst is over by then;
      // fall back to the normal hourly schedule computed from the moment the burst ends.
      if (burstActive && candidateTime.getTime() > burstEnd.getTime()) {
        return calculateNextSyncTime(schedules, burstEnd, null);
      }
      // No quiet time blocking this sync
      return candidateTime;
    }

    // Move past the quiet time window.
    if (latestQuietEnd) {
      candidateTime = new Date(latestQuietEnd);
      if (!burstActive) {
        // Hourly cadence resumes on the next hour boundary after quiet time ends.
        if (candidateTime.getUTCMinutes() > 0 || candidateTime.getUTCSeconds() > 0) {
          candidateTime.setUTCHours(candidateTime.getUTCHours() + 1);
        }
        candidateTime.setUTCMinutes(0, 0, 0);
      }
      // During a burst the next tick fires as soon as quiet time ends, so the quiet-end
      // moment itself is the candidate.
    } else {
      // Fallback: advance by one interval.
      candidateTime = new Date(
        candidateTime.getTime() + (burstActive ? BURST_INTERVAL_MINUTES : 60) * 60 * 1000
      );
    }

    iterations++;
  }

  // Fallback if we couldn't find a time (shouldn't happen)
  return candidateTime;
}

/**
 * Formats a future date as a relative time string
 * e.g., "in 2 hours", "in 45 minutes", "tomorrow at 3:00 AM"
 */
export function formatNextSyncTime(nextSync: Date, now: Date = new Date()): string {
  const diffMs = nextSync.getTime() - now.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'Shortly';
  if (diffMins < 60) return `in ${String(diffMins)} min${diffMins === 1 ? '' : 's'}`;
  if (diffHours < 24) {
    const remainingMins = diffMins % 60;
    if (remainingMins === 0) {
      return `in ${String(diffHours)} hour${diffHours === 1 ? '' : 's'}`;
    }
    return `in ${String(diffHours)}h ${String(remainingMins)}m`;
  }

  // For times more than 24 hours away, show the actual time
  const tomorrow = new Date(now);
  tomorrow.setUTCDate(tomorrow.getUTCDate() + 1);
  tomorrow.setUTCHours(0, 0, 0, 0);

  if (nextSync < tomorrow) {
    // Later today
    return `today at ${nextSync.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      timeZone: 'UTC',
      hour12: true
    })} UTC`;
  } else if (diffDays === 1) {
    // Tomorrow
    return `tomorrow at ${nextSync.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      timeZone: 'UTC',
      hour12: true
    })} UTC`;
  } else {
    // Further in the future
    return nextSync.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZone: 'UTC',
      hour12: true
    }) + ' UTC';
  }
}
